import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	CATALOG_MECHANICAL_TEST_INITIAL_VALUES,
	CatalogMechanicalTest,
} from "../../../models/CatalogMechanicalTest";
import { upsertCatalogMechanicalTest } from "../../../repositories/CatalogMechanicalTestRepository";
import { useModalStyles } from "../../../styles/modalStyles";

type Props = {
	show: boolean;
	onHide: () => void;
	refreshData: Function;
};
export const CreateMechanicalTest = ({ show, onHide, refreshData }: Props) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CatalogMechanicalTest>({
		defaultValues: CATALOG_MECHANICAL_TEST_INITIAL_VALUES,
	});
	const handleClose = (
		event: Event | SyntheticEvent<any, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<CatalogMechanicalTest> = async (data: any) => {
		setIsSubmitLoading(true);
		const response = await upsertCatalogMechanicalTest(data);
		if (response.ok) {
			const userId = response.data.tables[0].rows[0].Id;
			setIsSubmitLoading(false);
			setOpen(true);
			onHide();
			refreshData();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={onHide}
				maxWidth="sm"
				aria-labelledby="contained-modal-title-vcenter"
				title="NEW MECHANICAL TEST"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						<Grid container spacing={1}>
							<Grid size={{ xs: 12, md: 12 }}>
								<TextField
									{...register("MechanicalTestName", { required: true })}
									label="Name"
									variant="outlined"
									error={!!errors.MechanicalTestName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<TextField
									{...register("Decimals", { required: true })}
									label="# Decimals"
									variant="outlined"
									error={!!errors.Decimals}
									fullWidth
									margin="dense"
									type="number"
									onFocus={(e) => e.target.select()}
									autoComplete="off"
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<TextField
									{...register("Order", { required: true })}
									label="Order"
									variant="outlined"
									error={!!errors.Order}
									fullWidth
									margin="dense"
									type="number"
									onFocus={(e) => e.target.select()}
									autoComplete="off"
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="UseInCircular"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={field.value}
											onCheckboxChange={field.onChange}
											label="Use In Circular"
											className={classes.checkbox}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onHide}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The mechanical test was created successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
